import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { format, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import {
  extractCollisions,
  useCreateManualSession,
} from './use-admin-session-mutations'
import { useAdminUsers } from './use-admin-users'
import { fromLocalInputFormat } from './datetime-local'
import type { AdminWorkSession } from './use-admin-sessions'

const schema = z
  .object({
    userId: z.string().min(1, 'Wybierz pracownika'),
    startedAt: z.string().min(1, 'Podaj godzinę rozpoczęcia'),
    endedAt: z.string().optional().or(z.literal('')),
    notes: z.string().max(2000, 'Maksymalnie 2000 znaków').optional(),
  })
  .refine(
    (v) => {
      if (!v.endedAt) return true
      return new Date(v.endedAt).getTime() > new Date(v.startedAt).getTime()
    },
    { message: 'Koniec sesji musi być po jej rozpoczęciu.', path: ['endedAt'] },
  )

type FormValues = z.infer<typeof schema>

interface Props {
  /** YYYY-MM-DD — domyślny dzień dla pól datetime. */
  defaultDate: string
  onClose: () => void
}

function fmtCollision(s: AdminWorkSession): string {
  const start = format(parseISO(s.startedAt), "d MMM 'o' HH:mm", { locale: pl })
  const end = s.endedAt
    ? format(parseISO(s.endedAt), 'HH:mm')
    : '(otwarta)'
  return `${start} — ${end}`
}

export default function AddSessionModal({ defaultDate, onClose }: Props) {
  const create = useCreateManualSession()
  const usersQuery = useAdminUsers({ active: 'true' })
  const [collisions, setCollisions] = useState<AdminWorkSession[] | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: '',
      startedAt: `${defaultDate}T09:00`,
      endedAt: '',
      notes: '',
    },
  })

  const userOptions = useMemo(() => {
    if (!usersQuery.data) return []
    return [...usersQuery.data.users].sort((a, b) =>
      a.name.localeCompare(b.name, 'pl'),
    )
  }, [usersQuery.data])

  async function onSubmit(values: FormValues) {
    setCollisions(null)
    try {
      await create.mutateAsync({
        userId: values.userId,
        startedAt: fromLocalInputFormat(values.startedAt),
        ...(values.endedAt
          ? { endedAt: fromLocalInputFormat(values.endedAt) }
          : {}),
        ...(values.notes ? { notes: values.notes } : {}),
      })
      toast.success('Sesja dodana')
      onClose()
    } catch (err) {
      const cols = extractCollisions(err)
      if (cols) {
        setCollisions(cols)
        return
      }
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-session-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 id="add-session-title" className="text-xl font-semibold mb-1">
          Dodaj sesję ręcznie
        </h2>
        <p className="text-sm text-text-muted mb-4">
          Sesja zostanie zapisana bez GPS jako „dodana ręcznie". Jeśli pominiesz
          koniec — pozostanie otwarta i pracownik będzie mógł ją domknąć.
        </p>

        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div>
            <Label htmlFor="add-userId">Pracownik</Label>
            <select
              id="add-userId"
              {...form.register('userId')}
              className="w-full rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm"
              disabled={usersQuery.isLoading}
            >
              <option value="">— wybierz —</option>
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
            {form.formState.errors.userId && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.userId.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="add-startedAt">Start</Label>
            <Input
              id="add-startedAt"
              type="datetime-local"
              invalid={!!form.formState.errors.startedAt}
              {...form.register('startedAt')}
            />
            {form.formState.errors.startedAt && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.startedAt.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="add-endedAt">Koniec (puste = otwarta sesja)</Label>
            <Input
              id="add-endedAt"
              type="datetime-local"
              invalid={!!form.formState.errors.endedAt}
              {...form.register('endedAt')}
            />
            {form.formState.errors.endedAt && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.endedAt.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="add-notes">Notatka (opcjonalna)</Label>
            <textarea
              id="add-notes"
              rows={2}
              className="w-full rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm placeholder:text-text-faint"
              placeholder="np. „pracownik zapomniał kliknąć start"
              {...form.register('notes')}
            />
          </div>

          {collisions && collisions.length > 0 && (
            <Alert variant="error">
              <div className="space-y-1">
                <div className="font-medium">Sesja koliduje z istniejącymi:</div>
                <ul className="list-disc pl-4 text-xs">
                  {collisions.map((c) => (
                    <li key={c.id}>{fmtCollision(c)}</li>
                  ))}
                </ul>
                <div className="text-xs">
                  Skoryguj godziny lub edytuj istniejącą sesję.
                </div>
              </div>
            </Alert>
          )}

          {create.error && !collisions && (
            <Alert variant="error">{apiErrorMessage(create.error)}</Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit" loading={create.isPending}>
              Dodaj sesję
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
